import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { debugBoundingBoxSignature } from '@/lib/shapes/debug-bounding-boxes'

import type { ChromeSelectionState } from '../types/polled-canvas'

/**
 * The fingerprint `usePolledCanvas` compares each frame; state is only set when it changes.
 *
 * Selection and the group box join the fingerprint so chrome follows a selection change or a
 * group rotation, neither of which necessarily moves any individual shape's own geometry.
 * `overflowIds`/`editingShapeTextId` join it for the same reason on the overflow badge's
 * behalf: typing past capacity or entering/leaving edit mode can flip whether a shape shows
 * the badge without moving that shape (or any other) at all.
 *
 * `chainPeerIds` joins for the same reason once more, and it is the easiest one to miss:
 * linking or unlinking two frames changes who gets chain chrome without moving any geometry
 * at all, so without it here the highlight would simply go stale until something else nudged
 * the fingerprint. `text-linking/helpers/linking-signature.ts` widened its own fingerprint over
 * exactly this (WMPCM-9199).
 */
export function computePolledCanvasSignature({
  shapes,
  selectedIds,
  chrome,
  selectionState,
  altHoveredId,
  overflowIds,
  editingShapeTextId,
  chainPeerIds,
}: {
  shapes: ShapeData[]
  selectedIds: string[]
  chrome: MultiSelectChrome | null
  selectionState: ChromeSelectionState | null
  altHoveredId: string | null
  overflowIds: ReadonlySet<string>
  editingShapeTextId: string | null
  chainPeerIds: Set<string>
}): string {
  return `${debugBoundingBoxSignature(shapes)}|${selectedIds.join(',')}|${
    chrome
      ? `${chrome.pivotX},${chrome.pivotY},${chrome.width},${chrome.height},${chrome.rotationDeg}`
      : ''
  }|${selectionState?.badge ?? ''}|${selectionState?.geometryBlockedPulseKey ?? 0}|${altHoveredId ?? ''}|${[...overflowIds].sort().join(',')}|${editingShapeTextId ?? ''}|${[...chainPeerIds].sort().join(',')}`
}
